/* eslint-disable @typescript-eslint/no-shadow */

import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom-latest';
import styled from '@emotion/styled';
import Axios from 'axios';
import { getApiV5TermsAndConditions } from '@liscio/api';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getStorageData, setUserInLocalStorage } from 'utilities/utils';
import { HOME_API_PATH, MIXPANEL_URL_EVENT } from 'constants/constants';
import { trackEvent } from 'analytics/trackEvent';
import * as NotificationActions from 'actions/NotificationActions';
import * as actions from 'actions/PaginationActions';
import * as HomeActions from 'actions/HomeActions';
import Sidebar from 'components/Sidebar';
import TermConditions from 'common/TermsConditions_hooks_updated';
import { useLaunchDarklyIdentify } from 'hooks/useLaunchDarklyIdentify';
import { initGraphQLClient } from 'hooks/useGraphQL';
import { getAuthSession } from 'auth/session';
import useGenerateExternalUrl from 'hooks/useGenerateExternalUrl';
import OutofOfficeBanner from './OutofOffice/OutofOfficeBanner';
import FileConnectBanner from './FileConnectBanner/FileConnectBanner';
import 'scss/all.scss';
import SMSNotificationBar from './SMS/components/SMSNotificationBar/SMSNotificationBar';
import {
  POST_TERMS_DASHBOARD_PARAMS,
  POST_TERMS_DASHBOARD_PATH,
} from './postTermsDashboard';

import './axios-interceptors';

const StyledMain = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;

  .emailWrapscroll {
    /* padding-bottom: 60px; */
  }
  .Main__Content {
    height: 100vh;
    display: flex;
    flex-direction: row;
  }
`;

// eslint-disable-next-line no-shadow
const Main = ({ NotificationActions, saveHomeData }) => {
  const location = useLocation();
  const [homeData, setHomeData] = useState();
  const [termsData, setTermsData] = useState({});
  const navigate = useNavigate();
  const { identify } = useLaunchDarklyIdentify();
  const postTermsDashboardUrl = useGenerateExternalUrl(POST_TERMS_DASHBOARD_PATH, {
    extraParams: POST_TERMS_DASHBOARD_PARAMS,
  });

  const local_data = getStorageData();

  const hasSmsEnabled = useMemo(
    () => homeData?.cpa_sms_enabled || false,
    [homeData],
  );

  useEffect(() => {
    getTermConditions();
    getHomeData(identify);
    if (localStorage.getItem('path') && localStorage.getItem('search')) {
      const path = localStorage.getItem('path');
      const search = localStorage.getItem('search');
      const full_path = path + search;
      localStorage.removeItem('path');
      localStorage.removeItem('search');
      navigate(`${full_path}`);
    }
    if (localStorage.getItem('emailsUrl')) {
      localStorage.removeItem('emailsUrl');
    }
  }, []);

  // componentDidUpdate
  useEffect(() => {
    trackUrlEvent();
  }, [location.pathname, homeData]);

  function trackUrlEvent() {
    trackEvent(MIXPANEL_URL_EVENT, {
      path: parseUrl(location.pathname),
    });
  }

  // removes id specific numbers from url and replaces them with ':id'
  function parseUrl(path) {
    let url = '';
    const array = path.split('/');
    const len = array.length;
    let i = 0;
    array.forEach((key) => {
      if (key === '' || Number.isNaN(Number(key))) {
        url += key;
      } else {
        url += ':id';
      }
      if (i !== len - 1) {
        url += '/';
      }
      i += 1;
    });
    return url;
  }

  async function getTermConditions() {
    if (getAuthSession()) {
      try {
        const response = await getApiV5TermsAndConditions();
        if (response.data.term) {
          setTermsData(response);
        }
      } catch (error) {
        console.log(error);
      }
    }
  }

  async function getHomeData(identifyFn) {
    if (getAuthSession()) {
      // we need an auth_token to init the graphql client
      initGraphQLClient();

      try {
        // TODO: __HOMEDATA-AUDIT
        const response = await Axios.get(HOME_API_PATH);
        if (response.status === 200 && Object.keys(response.data.data).length) {
          const stored = response.data.data;
          stored.smsPhoneLine = response.data.smsPhoneLine;
          stored.is_multiple_cpa = response.data.is_multiple_cpa;
          stored.multiple_cpa_data = response.data.cpa_data;
          NotificationActions.isMultipleCpa(response.data.is_multiple_cpa);
          NotificationActions.multipleCpaData(response.data.cpa_data);
          NotificationActions.isAway(response.data.is_away);
          NotificationActions.awayStartDate(
            response.data.away_start_date
              ? response.data.away_start_date
              : null,
          );
          NotificationActions.awayEndDate(
            response.data.away_end_date ? response.data.away_end_date : null,
          );
          localStorage.setItem(
            'release',
            JSON.stringify(response.data.release_version),
          );
          setUserInLocalStorage(stored);
          window.dispatchEvent(new Event('storage'));
          setHomeData(stored);
          saveHomeData(response.data.data);
          // Identify context for Launch Darkly
          if (identifyFn && typeof identifyFn === 'function') {
            await identifyFn(response?.data?.data);
          }
        }
      } catch (error) {
        console.log(error);
      }
    }
  }

  function agreedTerms() {
    setTermsData({
      ...termsData,
      data: {
        ...termsData.data,
        term: {
          ...termsData.data.term,
          version_number: 0,
        },
        agreement: { has_agreed: true, agreed_terms_version: 0 },
      },
    });
    window.location.replace(postTermsDashboardUrl);
  }

  const showTermsComp =
    termsData &&
    Object.keys(termsData).length &&
    (!termsData.data.agreement.has_agreed ||
      termsData.data.agreement.agreed_terms_version !==
        termsData.data.term?.version_number);

  const showTermsAndConditions =
    showTermsComp &&
    location.pathname !== '/client_terms' &&
    location.pathname !== '/privacy_policy';
  const showLoading =
    location.pathname !== '/' &&
    location.pathname !== 'dashboard' &&
    !local_data;

  return (
    <div className="AppContainer" style={{ display: 'flex', width: '100%' }}>
      {showTermsAndConditions && (
        <TermConditions
          data={{ termsData }}
          agreedTerms={agreedTerms}
          isFirmEmployee={homeData?.is_employee}
        />
      )}
      {!showTermsAndConditions && !showLoading && (
        <StyledMain className="Main">
          <SMSNotificationBar />
          <Sidebar hasSmsEnabled={hasSmsEnabled} />
          {/* Convert this to styled component */}
          <div
            style={{
              flexDirection: 'column',
              width: '100%',
              height: '100%',
              overflow: 'auto',
              position: 'relative',
            }}
          >
            <article
              className="emailWrapscroll"
              style={{ flex: 1, height: '100%' }}
            >
              <OutofOfficeBanner />
              <FileConnectBanner isEmployee={homeData?.is_employee === true} />
              <Outlet />
            </article>
          </div>
        </StyledMain>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  isAway: state.notificationReducer.isAway,
  awayStartDate: state.notificationReducer.awayStartDate,
  awayEndDate: state.notificationReducer.awayEndDate,
  updateAwaySetting: state.notificationReducer.updateAwaySetting,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(actions, dispatch),
  NotificationActions: bindActionCreators(NotificationActions, dispatch),
  saveHomeData: (data) => dispatch(HomeActions.fetchAll(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Main);
